import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Languages, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SUPPORTED_LOCALES, LOCALE_LABELS, LOCALE_STORAGE_KEY } from "../i18n";
import { updateLocale } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { track } from "../lib/analytics";

// Flag emoji per locale. We use Brazilian (🇧🇷) for `pt` because the
// translated bundle is pt-BR — Portugal users still get a usable
// experience but the flag signals which dialect they're getting.
const LOCALE_FLAGS = {
  en: "🇺🇸",
  fr: "🇫🇷",
  es: "🇪🇸",
  pt: "🇧🇷",
};

/**
 * LanguageSelectorCard — Settings card where users pick the UI &
 * Trouvé voice language. Persists in two places:
 *   1. Immediate: `i18n.changeLanguage()` + localStorage so the UI
 *      flips instantly without a page reload.
 *   2. Server: PUT /api/profile/locale so Trouvé writes routines,
 *      speaks (TTS), and emails the user in this language across
 *      devices. The server is the source of truth on next sign-in.
 *
 * Stealth-mode users still get the immediate UI flip; the server call
 * silently no-ops on 401 (the api wrapper logs and moves on).
 */
const LanguageSelectorCard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [pending, setPending] = useState(null); // locale being saved

  const current = i18n.language?.split("-")[0] || "en";

  const onPick = async (locale) => {
    if (locale === current || pending) return;
    setPending(locale);
    try {
      // Flip UI right away — feels snappy.
      await i18n.changeLanguage(locale);
      try {
        window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
      } catch {
        // localStorage may be unavailable (private mode, etc.) — UI still flips.
      }
      // Persist server-side (best-effort; stealth/guest users skip).
      if (user) {
        try {
          await updateLocale(locale);
        } catch {
          // 401/network — UI stays flipped locally, will retry on next change.
        }
      }
      track("locale_changed", { locale });
      toast.success("✓");
    } finally {
      setPending(null);
    }
  };

  return (
    <div
      className="bg-white/80 backdrop-blur-xl border border-[#EADDD7] rounded-3xl p-6 sm:p-7 soft-shadow mb-5"
      data-testid="language-card"
    >
      <div className="flex items-start gap-3 mb-1">
        <div className="h-10 w-10 rounded-full bg-[#F2E8E9] flex items-center justify-center shrink-0">
          <Languages className="h-5 w-5 text-[#9B6A6C]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-medium text-[#2C2123]">
            {t("settings.languageTitle")}
          </h3>
          <p className="text-sm text-[#5C4B51] leading-relaxed mt-1 max-w-xl">
            {t("settings.languageBody")}
          </p>
        </div>
      </div>

      <div
        className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5"
        data-testid="language-options"
      >
        {SUPPORTED_LOCALES.map((code) => {
          const active = code === current;
          const isPending = pending === code;
          return (
            <button
              key={code}
              type="button"
              onClick={() => onPick(code)}
              disabled={isPending}
              className={`relative flex items-center justify-between gap-2 px-4 py-3 rounded-xl border transition-colors text-left ${
                active
                  ? "bg-[#9B6A6C] text-white border-[#9B6A6C]"
                  : "bg-[#FAF5F1] text-[#2C2123] border-[#EADDD7] hover:border-[#D5A8A4]"
              } ${isPending ? "opacity-70 cursor-wait" : ""}`}
              data-testid={`language-option-${code}`}
              aria-pressed={active}
            >
              <span className="inline-flex items-center gap-2">
                <span className="text-base leading-none" aria-hidden="true">
                  {LOCALE_FLAGS[code]}
                </span>
                <span className="text-sm font-medium">{LOCALE_LABELS[code]}</span>
              </span>
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              ) : active ? (
                <Check className="h-4 w-4 shrink-0" />
              ) : (
                <span className="text-xs uppercase tracking-wider text-[#8A7D82]">
                  {code.toUpperCase()}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LanguageSelectorCard;
